import {
  createWorkflowTemplate,
  createWorkflowTemplateVersion,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Input,
} from '@commenda/kakashi';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

interface CreateWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Where to land after creation; defaults to the client app's editor route. */
  getEditorHref?: (versionId: string) => string;
}

export const CreateWorkflowModal = ({
  isOpen,
  onClose,
  getEditorHref,
}: CreateWorkflowModalProps) => {
  const router = useRouter();
  const [name, setName] = useState('');
  const [externalName, setExternalName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !externalName.trim()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const templateResult = await createWorkflowTemplate({
        name: name.trim(),
        externalName: externalName.trim(),
      });

      if (!templateResult.success || !templateResult.data) {
        // The server names the collision ("Workflow template with key ... already exists."). Showing its
        // message is the difference between an author renaming the workflow and an author opening devtools.
        setError(templateResult.error ?? 'Failed to create workflow template.');
        return;
      }

      const templateId = templateResult.data.workflowTemplate.id;

      const versionResult = await createWorkflowTemplateVersion({
        workflowTemplateId: templateId,
        version: '1.0.0',
      });

      if (!versionResult.success || !versionResult.data) {
        setError(
          versionResult.error
            ? `Template created, but the version failed: ${versionResult.error}`
            : 'Template created, but failed to create version.'
        );
        return;
      }

      const versionId = versionResult.data.workflowTemplateVersion.id;
      onClose();
      router.push(
        getEditorHref?.(versionId) ?? `/workflow-builder/version/${versionId}`
      );
    } catch {
      setError('An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setExternalName('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b border-b-border-primary">
          <DialogTitle>Create a workflow</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 px-6 py-6">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="workflow-name"
                className="txt-small-medium text-text-primary"
              >
                Name
              </label>
              <Input
                id="workflow-name"
                placeholder="e.g. Director Change Workflow"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="workflow-external-name"
                className="txt-small-medium text-text-primary"
              >
                External Name
              </label>
              <Input
                id="workflow-external-name"
                placeholder="e.g. Director Change"
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                required
              />
              <span className="txt-extra-small-regular text-text-tertiary">
                The name shown to external users.
              </span>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-text-error txt-small-regular">
                <Icons.AlertCircle className="size-4 shrink-0" />
                {error}
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-t-border-primary">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !name.trim() || !externalName.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
